/**
 * İstemci tarafı yükleme akışı — ADIM 3.
 *
 * `state.ts`'teki 3 dolu slotu Worker'a yükler:
 *   1. POST /api/session (Turnstile token ile) → lead_id
 *   2. Her slot için PUT /api/upload/:lead_id/:slot (paralel)
 *   3. Başarılı olanları slot.status = "uploaded" + slot.r2Key ile işaretler
 *
 * Fotoğraf verisi hiçbir zaman `lead.ts`/webhook payload'ına girmez — yalnızca
 * bu dosyada Worker'a (R2'ye) gider; `lead.ts` yalnızca `r2Key` referansı taşır.
 */
import { getTurnstileToken } from "./turnstile";
import { state, type SlotState } from "./state";
import type { PhotoSlotId } from "./config";

const UPLOAD_API_BASE = (
  (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_UPLOAD_API_BASE ?? ""
).trim().replace(/\/+$/, "");

export interface UploadResult {
  ok: boolean;
  leadId: string | null;
  failedSlots: PhotoSlotId[];
}

/** Slot bazında yükleme durumu — main.ts ilerleme göstergesi için kullanır. */
export type UploadProgressStatus = "pending" | "uploading" | "uploaded" | "failed";

export interface UploadProgress {
  slotId: PhotoSlotId;
  status: UploadProgressStatus;
}

async function fetchLeadId(turnstileContainer: HTMLElement): Promise<string> {
  const turnstileToken = await getTurnstileToken(turnstileContainer);
  const res = await fetch(`${UPLOAD_API_BASE}/api/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ turnstileToken }),
  });
  if (!res.ok) throw new Error(`session_failed_${res.status}`);
  const data = (await res.json()) as { lead_id: string };
  return data.lead_id;
}

async function uploadOne(leadId: string, slot: SlotState): Promise<boolean> {
  // slot.blob — EXIF-orientation düzeltilmiş, downscale edilmiş, EXIF/GPS'siz
  // yeniden kodlanmış JPEG. Orijinal dosya state'te hiç tutulmuyor (bkz.
  // state.ts SlotState yorumu) — burada başka bir şey göndermek mümkün değil.
  if (!slot.blob) return false;
  try {
    const res = await fetch(`${UPLOAD_API_BASE}/api/upload/${leadId}/${slot.id}`, {
      method: "PUT",
      headers: { "Content-Type": "image/jpeg" },
      body: slot.blob,
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { r2Key: string };
    slot.r2Key = data.r2Key;
    return true;
  } catch {
    return false;
  }
}

/** Bir slotu yükler; başarısız olursa 1 kez otomatik tekrar dener. */
async function uploadWithRetry(leadId: string, slot: SlotState, onProgress: (p: UploadProgress) => void): Promise<boolean> {
  onProgress({ slotId: slot.id, status: "uploading" });
  let ok = await uploadOne(leadId, slot);
  if (!ok) ok = await uploadOne(leadId, slot);
  onProgress({ slotId: slot.id, status: ok ? "uploaded" : "failed" });
  return ok;
}

/**
 * 3 dolu slotu Worker'a yükler. `turnstileContainer`: Turnstile widget'ının
 * monte edileceği (görünmez de olabilir) DOM elemanı.
 */
export async function uploadAllSlots(
  turnstileContainer: HTMLElement,
  onProgress: (p: UploadProgress) => void,
): Promise<UploadResult> {
  let leadId: string;
  try {
    leadId = await fetchLeadId(turnstileContainer);
  } catch {
    return { ok: false, leadId: null, failedSlots: state.slots.map((s) => s.id) };
  }

  const results = await Promise.all(
    state.slots.map(async (slot) => {
      const ok = await uploadWithRetry(leadId, slot, onProgress);
      return { id: slot.id, ok };
    }),
  );

  const failedSlots = results.filter((r) => !r.ok).map((r) => r.id);
  return { ok: failedSlots.length === 0, leadId, failedSlots };
}
